import React, { useCallback, useEffect, useMemo, useState } from "react";
import useTablePagination from "@/hooks/useTablePagination.ts";
import {
  Alert,
  Box,
  Snackbar,
  TableBody,
  TableContainer,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  StyledRowActionsWrapperBox,
  StyledTable,
  StyledTableCard,
  StyledTableCell,
  StyledTableHeader,
  StyledTableRow,
} from "@/components/styled";
import TablePagination from "@/components/TablePagination";
import dayjs from "dayjs";
import useDebouncedSearch from "@/hooks/useDebouncedSearch.ts";
import useTableFilter from "@/hooks/useTableFilter.ts";
import useDateRange from "@/hooks/useDateRange.ts";
import { useProjectsStore } from "@/store/projectsStore.ts";
import { FileRecord, ProjectRecord, getApiErrorMessage } from "@/types";
import { formatFileSize } from "@/utils/formatFileSize";
import { updateProjectFileReq, uploadProjectFileReq } from "@/api/projectsApi";
import {
  INCENTIVE_STATUS,
  PROJECTS_DOCUMENTATION_FILTER_ITEMS,
} from "@/constants";
import { useAuthStore } from "@/store/authStore.ts";
import FileTableItem from "@/components/FileTableItem";
import { formatDate, formatDateLocal } from "@/utils/formatDate.ts";
import ProjectInfoCard from "@/pages/ProjectDetailsPage/components/ProjectInfoCard.tsx";
import FileManagementRowActions from "@/pages/admin-pages/FileManagementPage/copmonents/FileManagement/FileManagementRowActions.tsx";
import ProjectsNavigation from "@/pages/ProjectDetailsPage/components/ProjectsNavigation.tsx";
import { useNavigate } from "react-router-dom";
import useFetchDataWithLoading from "@/hooks/useFetchDataWithLoading.ts";
import CollapsableRow, { CellWithHeader } from "@/components/CollapsableRow";
import ProjectBadgesWrapper from "@/pages/ProjectDetailsPage/components/ProjectBadgesWrapper.tsx";
import CommonTableActions from "@/components/CommonTableActions";
import TableFilter from "@/components/TableFilter";
import UploadProjectFile from "@/components/UploadProjectFile";
import { getFileTypeLabel } from "@/utils/getFileTypeLabel.ts";
import { allowUploadProject } from "@/utils/allowUploadProject.ts";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import SendPerformanceReportEmail from "@/pages/ProjectDetailsPage/components/SendPerformanceReportEmail.tsx";
import EditPerformanceReportFile from "@/pages/ProjectDetailsPage/components/EditPerformanceReportFile.tsx";

const FILE_UPLOAD_ERROR_MESSAGE = "File upload failed. Please try again.";

const UPFRONT_RESERVED_WARNING =
  "You cannot approve this document because your project status is not 'upfront reserved'. Please update the project status before proceeding.";

const REQUIRES_UPFRONT_RESERVED_FILE_TYPES = [
  "serialNumberOemId",
  "ptoLetter",
];

function isValidDate(date: string) {
  return dayjs(date).isValid();
}

function formatOrEmpty(date: string) {
  return isValidDate(date) ? formatDateLocal(date) : "-";
}

function getFormattedIncentiveValue(value: string) {
  return value ? formatCurrency(Number(value)) : "";
}

function createFileData(
  id: number,
  fileName: string,
  fileSize: string,
  createdAt: string,
  status: string,
  fileType: string,
  accountName: string,
  accountEmail: string,
  url: string,
  dateSubmitted: string,
  dateReviewed: string,
  serialNumber: string,
  quarterlyPerformanceIncentiveValue: string,
  quarterName: string | null,
  performanceYear: number | null
) {
  const formattedFileSize = formatFileSize(fileSize);

  return {
    id,
    fileName,
    fileSize: formattedFileSize,
    status,
    createdAt: formatOrEmpty(createdAt),
    fileType,
    accountName,
    accountEmail,
    url,
    dateSubmitted: formatOrEmpty(dateSubmitted),
    dateReviewed: formatOrEmpty(dateReviewed),
    serialNumber,
    quarterlyPerformanceIncentiveValue,
    quarterName,
    performanceYear,
  };
}

const ProjectDocumentationTable = ({
  projectId,
  isFinancePage = false,
}: {
  projectId: ProjectRecord["id"];
  isFinancePage?: boolean;
}) => {
  const fetchDataWithLoading = useFetchDataWithLoading();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");

  const enrolmentStatus = useProjectsStore(
    (state) => state.projectDetails?.enrolmentStatus
  );

  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const matchesDownLg = useMediaQuery(theme.breakpoints.down("xl"));
  const role = useAuthStore((state) => state.role);
  const { projectFiles, totalProjectFilesCount } = useProjectsStore(
    (state) => state.projectFiles
  );
  const getProjectFiles = useProjectsStore((state) => state.getProjectFiles);
  const projectDetails = useProjectsStore((state) => state.projectDetails);

  const projectsIds = useProjectsStore((state) => state.projectsIds);

  const getProjectsIds = useProjectsStore((state) => state.getProjectsIds);

  const isUpfrontPaid =
    projectDetails?.incentiveStatus === INCENTIVE_STATUS.PAID;

  const rows = useMemo(() => {
    return projectFiles?.map(
      ({
        id,
        fileName,
        size,
        createdAt,
        status,
        type,
        url,
        timeReviewd,
        timeSubmited,
        serialNumber,
        quarterlyPerformanceIncentiveValue,
        quarterName,
        performanceYear,
        accountName = null,
        accountEmail = null,
      }) => {
        const fileSize = size;

        return createFileData(
          id,
          fileName,
          fileSize,
          createdAt,
          status,
          type,
          accountName ?? "",
          accountEmail ?? "",
          url,
          timeSubmited ?? "",
          timeReviewd ?? "",
          serialNumber ?? "",
          quarterlyPerformanceIncentiveValue ?? "",
          quarterName,
          performanceYear
        );
      }
    );
  }, [projectFiles, projectDetails?.projectSignature?.size]);

  const { page, rowsPerPage, handleChangeRowsPerPage, handleChangePage } =
    useTablePagination(rows, totalProjectFilesCount);
  const { dateRangeValue, handleDateRangeChange } = useDateRange();
  const { filters, handleFilterChange } = useTableFilter(handleChangePage);
  const { searchValue, handleDebounceSearchValue } =
    useDebouncedSearch(handleChangePage);

  const getFilesReq = useCallback(async () => {
    await fetchDataWithLoading(getProjectFiles, [
      projectId,
      page + 1,
      rowsPerPage,
      searchValue,
      filters,
      dateRangeValue,
    ]);
  }, [
    fetchDataWithLoading,
    getProjectFiles,
    projectId,
    page,
    rowsPerPage,
    searchValue,
    filters,
    dateRangeValue,
  ]);

  useEffect(() => {
    getFilesReq();
  }, [getFilesReq]);

  const uploadProjectFile = async (
    projectId: ProjectRecord["id"],
    data: FormData
  ) => {
    try {
      await uploadProjectFileReq(projectId, data);
      getFilesReq();
    } catch (e) {
      console.error(e);
      const errorMsg = getApiErrorMessage(e, FILE_UPLOAD_ERROR_MESSAGE);
      setErrorMessage(errorMsg);
    }
  };

  const handleCloseErrorSnackbar = () => {
    setErrorMessage("");
  };

  const handleCloseWarningSnackbar = () => {
    setWarningMessage("");
  };

  const updateProjectFile = async (
    fileId: FileRecord["id"],
    data: Partial<FileRecord>
  ): Promise<boolean> => {
    try {
      await updateProjectFileReq(fileId, data);
      await getFilesReq();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const isAdmin = role === "administrator";

  const guardedUpdateProjectFile = async (
    fileId: FileRecord["id"],
    data: Partial<FileRecord>
  ): Promise<boolean> => {
    if (
      isAdmin &&
      data.status === "approved" &&
      projectDetails?.incentiveStatus !== INCENTIVE_STATUS.RESERVED
    ) {
      const file = rows.find((row) => row.id === fileId);
      if (
        file &&
        REQUIRES_UPFRONT_RESERVED_FILE_TYPES.includes(file.fileType)
      ) {
        setWarningMessage(UPFRONT_RESERVED_WARNING);
        return false;
      }
    }
    return updateProjectFile(fileId, data);
  };
  const hasPerformanceReport = rows.some(
    (row) => row.fileType === "performanceReport"
  );

  return (
    <>
      <Box sx={{ width: "100%", position: "relative" }}>
        <StyledTableCard sx={{ width: "100%" }}>
          <CommonTableActions
            title={"Project documentation"}
            onSearch={handleDebounceSearchValue}
            filter={
              <TableFilter
                placeholder={"Status"}
                items={PROJECTS_DOCUMENTATION_FILTER_ITEMS}
                setFilterValues={handleFilterChange}
              />
            }
            onDateRange={handleDateRangeChange}
            rightActions={
              <>
                {allowUploadProject(role, enrolmentStatus) && (
                  <UploadProjectFile
                    isButton={true}
                    uploadFile={(data) => uploadProjectFile(projectId, data)}
                    batteryOem={projectDetails?.batteryOem}
                  />
                )}
              </>
            }
          />
          <TableContainer>
            <StyledTable aria-label="customized table">
              <StyledTableHeader>
                <TableRow>
                  <StyledTableCell>File name</StyledTableCell>
                  <StyledTableCell>Document type</StyledTableCell>
                  <StyledTableCell>Serial number</StyledTableCell>
                  <StyledTableCell>
                    Quarterly performance incentive value
                  </StyledTableCell>
                  <StyledTableCell>File size</StyledTableCell>
                  <StyledTableCell>Date & time Submitted</StyledTableCell>
                  <StyledTableCell>Date & time Reviewed</StyledTableCell>
                  {hasPerformanceReport && (
                    <>
                      <StyledTableCell>Year</StyledTableCell>
                      <StyledTableCell>Quarter</StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell></StyledTableCell>
                  {hasPerformanceReport && isAdmin && (
                    <StyledTableCell></StyledTableCell>
                  )}
                  {hasPerformanceReport && isAdmin && (
                    <StyledTableCell></StyledTableCell>
                  )}
                </TableRow>
              </StyledTableHeader>
              <TableBody>
                {rows.map((row) =>
                  downSm ? (
                    <CollapsableRow
                      key={row.id}
                      title={row.fileName}
                      fileName={row.fileName}
                      firstSub={getFileTypeLabel(row.fileType)}
                      secondSub={
                        <ProjectBadgesWrapper
                          isDeleted={row.status === "deleted"}
                          isAdmin={isAdmin}
                          rowId={row.id}
                          rowStatus={row.status}
                          updateProjectFile={guardedUpdateProjectFile}
                        />
                      }
                    >
                      <CellWithHeader header={"File size"}>
                        {row.fileSize}
                      </CellWithHeader>
                      <CellWithHeader header={"Serial number"}>
                        {row.serialNumber}
                      </CellWithHeader>
                      <CellWithHeader
                        header={"Quarterly performance incentive value"}
                      >
                        {getFormattedIncentiveValue(
                          row.quarterlyPerformanceIncentiveValue
                        )}
                      </CellWithHeader>
                      <CellWithHeader header={"Date & time Submitted"}>
                        {row.dateSubmitted}
                      </CellWithHeader>
                      <CellWithHeader header={"Date & time Reviewed"}>
                        {row.dateReviewed}
                      </CellWithHeader>
                      {hasPerformanceReport && (
                        <>
                          <CellWithHeader header="Year">
                            {row?.performanceYear ?? "-"}
                          </CellWithHeader>
                          <CellWithHeader header="Quarter">
                            {row?.quarterName ?? "-"}
                          </CellWithHeader>
                        </>
                      )}
                      <StyledRowActionsWrapperBox>
                        {role && (
                          <FileManagementRowActions
                            status={row.status}
                            rowId={row.id}
                            getFilesReq={getFilesReq}
                          />
                        )}
                      </StyledRowActionsWrapperBox>
                      <StyledRowActionsWrapperBox>
                        {isAdmin && row.fileType === "performanceReport" && (
                          <SendPerformanceReportEmail
                            projectId={projectId}
                            performanceReportId={row.id}
                            quarterName={row.quarterName}
                            performanceYear={row.performanceYear}
                            isUpfrontPaid={isUpfrontPaid}
                          />
                        )}
                      </StyledRowActionsWrapperBox>
                      <StyledRowActionsWrapperBox>
                        {isAdmin && row.fileType === "performanceReport" && (
                          <EditPerformanceReportFile
                            projectId={projectId}
                            performanceReportId={row.id}
                            quarterName={row.quarterName}
                            performanceYear={row.performanceYear}
                            getFilesReq={getFilesReq}
                          />
                        )}
                      </StyledRowActionsWrapperBox>
                    </CollapsableRow>
                  ) : (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell>
                        <FileTableItem
                          fileName={row.fileName}
                          path={`project/document/download/${row.id}`}
                          url={row.url}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {getFileTypeLabel(row.fileType)}
                      </StyledTableCell>
                      <StyledTableCell>{row.serialNumber}</StyledTableCell>
                      <StyledTableCell>
                        {getFormattedIncentiveValue(
                          row.quarterlyPerformanceIncentiveValue
                        )}
                      </StyledTableCell>
                      <StyledTableCell>{row.fileSize}</StyledTableCell>
                      <StyledTableCell>{row.dateSubmitted}</StyledTableCell>
                      <StyledTableCell>{row.dateReviewed}</StyledTableCell>
                      {hasPerformanceReport && (
                        <>
                          <StyledTableCell>
                            {row?.performanceYear ?? "-"}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row?.quarterName ?? "-"}
                          </StyledTableCell>
                        </>
                      )}
                      <StyledTableCell>
                        <ProjectBadgesWrapper
                          isDeleted={row.status === "deleted"}
                          isAdmin={isAdmin}
                          rowId={row.id}
                          rowStatus={row.status}
                          updateProjectFile={guardedUpdateProjectFile}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <StyledRowActionsWrapperBox>
                          {role && (
                            <FileManagementRowActions
                              status={row.status}
                              rowId={row.id}
                              getFilesReq={getFilesReq}
                            />
                          )}
                        </StyledRowActionsWrapperBox>
                      </StyledTableCell>
                      <StyledTableCell>
                        <StyledRowActionsWrapperBox>
                          {isAdmin && row.fileType === "performanceReport" && (
                            <SendPerformanceReportEmail
                              projectId={projectId}
                              performanceReportId={row.id}
                              quarterName={row.quarterName}
                              performanceYear={row.performanceYear}
                              isUpfrontPaid={isUpfrontPaid}
                            />
                          )}
                        </StyledRowActionsWrapperBox>
                      </StyledTableCell>
                      <StyledTableCell>
                        <StyledRowActionsWrapperBox>
                          {isAdmin && row.fileType === "performanceReport" && (
                            <EditPerformanceReportFile
                              projectId={projectId}
                              performanceReportId={row.id}
                              quarterName={row.quarterName}
                              performanceYear={row.performanceYear}
                              getFilesReq={getFilesReq}
                            />
                          )}
                        </StyledRowActionsWrapperBox>
                      </StyledTableCell>
                    </StyledTableRow>
                  )
                )}
              </TableBody>
            </StyledTable>
          </TableContainer>
          <TablePagination
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalCount={totalProjectFilesCount}
          />
        </StyledTableCard>
        {!matchesDownLg && (
          <ProjectsNavigation
            navigateTo={(projectId, searchParams) => {
              const basePath = isFinancePage ? "/finance" : "/projects";
              navigate(`${basePath}/${projectId}?${searchParams.toString()}`);
            }}
            ids={projectsIds}
            getIds={getProjectsIds}
          />
        )}
      </Box>

      {!!errorMessage && (
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{ zIndex: 1000 }}
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={handleCloseErrorSnackbar}
        >
          <Alert onClose={handleCloseErrorSnackbar} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}

      {!!warningMessage && (
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{ zIndex: 1000 }}
          open={!!warningMessage}
          autoHideDuration={8000}
          onClose={handleCloseWarningSnackbar}
        >
          <Alert
            onClose={handleCloseWarningSnackbar}
            severity="warning"
            sx={{ maxWidth: "480px" }}
          >
            {warningMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default ProjectDocumentationTable;
